/**
 * Shared helpers for detecting and normalising "all zero" user inputs to a canonical form.
 *
 * Motivation (LQC-889 / cash-0 work): users can type many string representations that
 * are numerically zero — "0", "00", "000", "0.00", "-0", "0-00", "-0-00", "00-000" etc.
 * Without normalisation these pass through validation inconsistently and get stored
 * verbatim, producing payloads like "000" and displays that don't match what the user typed.
 *
 * Applies to:
 *   - Quote entry ($p, $fr, and any numeric notation) in DynamicCellEditorQuoteDollarFraction
 *   - Fill price entry ($fr and numeric) in FillPriceCellEditor
 *   - Validation flash logic in quoteFlashHelper (isQuotedAtZero / isQuotedNegative)
 */

/**
 * Returns true for any value whose digits (after stripping a leading "-" and all
 * separators "." and "-") are all zero and non-empty.
 *
 * Matches: "0", "00", "000", "0.00", "-0", "0-00", "-0-00", "00-000", "0.0000"
 * Does not match: "", null, undefined, "1", "0.1", "10-00", "0-01"
 */
export function isNumericallyZero(value: unknown): boolean {
  if (value == null || value === "") return false;
  const digits = String(value).trim().replace(/^-/, "").replace(/[-.]/g, "");
  return digits.length > 0 && /^0+$/.test(digits);
}

/**
 * Returns true if the trimmed string form starts with a literal "-".
 * Used to preserve user-entered negative intent (e.g. "-0") through normalisation.
 * Note: does NOT detect JS -0 numbers — those stringify to "0" and lose sign.
 */
export function hasLeadingMinus(value: unknown): boolean {
  if (value == null) return false;
  return String(value).trim().startsWith("-");
}

/**
 * Collapses any numerically-zero input to a canonical zero form for the given notation.
 *
 * - "$fr" → "0-00"
 * - anything else (including "$p", "%", "bp", "$", undefined) → "0"
 *
 * Preserves a leading "-" so "-0", "-000", "-0-00" normalise to "-0" or "-0-00"
 * (the validation layer then treats all -0 forms as zero, not negative).
 *
 * Non-zero values are returned unchanged (stringified if numeric).
 */
export function normalizeZeroQuote(
  value: unknown,
  notation?: string | null,
): string {
  if (!isNumericallyZero(value)) {
    return value == null ? "" : String(value);
  }
  const sign = hasLeadingMinus(value) ? "-" : "";
  const canonicalZero = notation === "$fr" ? "0-00" : "0";
  return `${sign}${canonicalZero}`;
}
